import { informationCountSearchesContainerElement, savedSearchedCardsContainerElement } from "@modules/global/dom.ts"
import { registerLocationRedirectModal } from "@modules/saved-searches/saved-items/locationRedirect.ts"
import { registerRemoveButtons } from "@modules/saved-searches/saved-items/removeSavedSearches.ts"
import {
    INFORMATION_TEXT,
    SAVED_SEARCH_TEMPLATE,
    getErrorInformationText,
    getNoSearchedTemplate,
    getRemoveAllButton,
} from "@modules/saved-searches/saved-items/user-search-templates.ts"

const MAX_CARDS = 10
const containerBody = document.querySelector<HTMLDivElement>("#deine-suchen")
const userSearchAction = document.querySelector<HTMLDivElement>("#user-search-login-actions")
const loadingSpinner = document.querySelector<HTMLDivElement>(".loading-spinner")
const informationTextAside = document.querySelector<HTMLDivElement>("#user-search-content aside")
const dropDownText = document.querySelector<HTMLElement>("#deine-suchen-dropdown .dropdown-text")
export function checkIfSidebarTrafficStarsNeedsToBeShown(countSavedSearches: number) {
    const trafficStarsSideBar = document.getElementById("traffic-stars-sidebar")
    if (!trafficStarsSideBar) return

    if (countSavedSearches < 4) {
        trafficStarsSideBar.classList.add("hidden")
        return
    }

    trafficStarsSideBar.classList.remove("hidden")
    return
}

export async function savedCardGeneration() {
    if (!userData.isLoggedIn) return

    if (loadingSpinner) loadingSpinner.classList.remove("hidden")

    const savedSearchesInformation = await fetchSavedSearchesInformation()

    if (
        savedSearchesInformation.messageId === "ENTRY_LIST_ERROR" ||
        savedSearchesInformation.messageId === "USER_ERROR"
    ) {
        if (loadingSpinner) loadingSpinner.classList.add("hidden")
        if (!informationTextAside) return
        informationTextAside.innerHTML = getErrorInformationText()
        if (!dropDownText) return
        dropDownText.classList.add("alert", "user-search-alert", "alert-warning")
        dropDownText.innerHTML = "Ein unbekannter Fehler ist aufgetreten."
        return
    }

    const countSavedSearches = savedSearchesInformation.meta.count || 0

    if (loadingSpinner) loadingSpinner.classList.add("hidden")

    if (countSavedSearches === 0) {
        if (!containerBody) return
        containerBody.innerHTML = getNoSearchedTemplate()

        return
    }
    if (countSavedSearches > 0) {
        if (!userSearchAction) return
        userSearchAction.innerHTML = getRemoveAllButton()

        if (!informationTextAside) return

        informationTextAside.innerHTML = INFORMATION_TEXT

        if (countSavedSearches === MAX_CARDS) {
            const parser = new DOMParser()
            const element = parser.parseFromString(INFORMATION_TEXT, "text/html")

            element.getElementById("information-count-searches")?.classList.add("max-entity-alert")
            informationTextAside.innerHTML = element.body.innerHTML
        }
    }

    checkIfSidebarTrafficStarsNeedsToBeShown(countSavedSearches)

    const savedCardsContainerEl = savedSearchedCardsContainerElement()
    const informationCountSearchesContainerEl = informationCountSearchesContainerElement() as HTMLDivElement | null

    if (!informationCountSearchesContainerEl) return
    if (!savedCardsContainerEl) return

    const renderedCards = await generateSavedSearchCards(savedSearchesInformation, String(SAVED_SEARCH_TEMPLATE))
    const informationText = generateInformationText(countSavedSearches)

    savedCardsContainerEl.innerHTML = renderedCards
    informationCountSearchesContainerEl.innerHTML = informationText
    informationCountSearchesContainerEl.setAttribute("data-count", String(countSavedSearches))

    registerRemoveButtons()

    registerLocationRedirectModal()
    ;((): void => {
        import("../../location/LocationSelector.ts")
    })()
}

async function fetchSavedSearchesInformation() {
    const result = await fetch(`/api/user-search/${getUserId()}/list`, {
        method: "GET",
        credentials: "include",
    })

    return await result.json()
}

function getUserId() {
    return userData.userId
}

export function generateInformationText(countSavedSearches: number): string {
    let informationCountText = "Du hast die maximale Anzahl an gespeicherten Suchen erreicht."

    if (countSavedSearches < MAX_CARDS) {
        informationCountText = `Du kannst noch <span>${MAX_CARDS - countSavedSearches} Suchen</span> speichern.`
    }
    if (countSavedSearches === 0) {
        if (!containerBody) return ""
        containerBody.innerHTML = getNoSearchedTemplate()
    }

    return informationCountText
}

function generateDateString(createdAt = "") {
    const timestamp = new Date(createdAt)
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(timestamp)
}

function generateSearchList(information: any, item: string) {
    if (!information) return ""
    let itemList = ""
    const keys = Object.keys(information)
    let countList = keys.length

    for (const [key, value] of Object.entries(information)) {
        itemList += item.replace("{KEY}", transformKey(key)).replace("{VALUE}", String(value))

        if (--countList > 0) {
            itemList += " | "
        }
    }

    return itemList + " "
}

function generateSearchListLabel(mappedInformation: any) {
    const item = `<span class="text-bold">{KEY}</span>: {VALUE}`

    let itemList = ""
    const keys = Object.keys(mappedInformation)
    let countList = keys.length

    for (const [_key, value] of Object.entries(mappedInformation)) {
        if (!value) continue

        if ("Alle Regionen" === Object.keys(value)[0]) {
            itemList += `<span
                contenteditable="true"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                class="text-bold">Alle Regionen</span>`

            if (--countList > 0) {
                itemList += " | "
            }

            continue
        }
        if ("Aktueller Standort" === Object.keys(value)[0]) {
            itemList += `<span class="text-bold">Aktueller Standort</span>`

            if (--countList > 0) {
                itemList += " | "
            }

            continue
        }
        itemList += generateSearchList(value, item)

        if (--countList > 0) {
            itemList += " | "
        }
    }

    return itemList
}

function transformKey(rawKey: string): string {
    if (rawKey === "Adresse") return rawKey

    rawKey = rawKey.replace(/oe/g, "ö").replace(/ue/g, "ü").replace(/ae/g, "ä").replace(/ss/g, "ß")

    return rawKey.charAt(0).toUpperCase() + rawKey.slice(1)
}

/**
 * @param {{entries: {createdAt: string, name: string, id: number, filter: string, href: string, location: string, searchText: string}[]}} savedSearchesInformation
 * @param {string} card
 * */
async function generateSavedSearchCards(
    savedSearchesInformation: {
        entries: {
            createdAt: string
            name: string
            id: number
            filter: string
            href: string
            location: string
            searchText: string
        }[]
    },
    card: string,
) {
    let cards = ""

    for (const entry of savedSearchesInformation.entries) {
        const mappedInformation = mapInformation(entry)

        cards += card
            .replace(/{DATA_ID}/g, String(entry.id))
            .replace("{IS_MOBILE_LOCATION}", isMobileLocationSearch(entry) ? "data-location-search='mobile'" : "")
            .replace("{HREF}", entry.href || "#")
            .replace("{DATE}", generateDateString(entry.createdAt))
            .replace("{DATA_TITLE}", entry.name)
            .replace("{REPLACE_SEARCH_LIST_LABEL}", generateSearchListLabel(mappedInformation))
            .replace("{REPLACE_DELETE_TEXT}", deleteTextBasedOnDeviceType())
            .replace("{TITLE}", entry.name.toLowerCase())
    }

    return cards
}

function mapInformation(entry: any) {
    const filter = safeParse(entry.filter)
    const location = safeParse(entry.location)

    const mapped: Record<string, any> = {}

    if (location !== null) {
        mapped.location = mapLocationInformation(location)
        // mapped.isMobileLocationSearch = isMobileLocationSearch(location)
    }

    if (isObjectAndNotEmpty(filter?.rubriken)) mapped.rubriken = mapRubrikInformation(filter?.rubriken)
    if (isObjectAndNotEmpty(filter?.merkmale)) mapped.merkmale = mapMerkmalInformation(filter?.merkmale)
    if (isObjectAndNotEmpty(filter?.openings)) mapped.openings = mapOpeningInformation(filter?.openings)

    if (location?.umkreis) mapped.umkreis = mapUmkreisInformation(location?.umkreis)
    if (location?.adresseName) mapped.adresse = mapAddressInformation(location?.adresseName)

    return mapped
}

function isObjectAndNotEmpty(obj: any): boolean {
    if (typeof obj !== "object") return false
    return Object.keys(obj).length !== 0
}

function safeParse(jsonStr: string) {
    if (!jsonStr.trim()) {
        return null
    }
    return JSON.parse(jsonStr)
}

function mapUmkreisInformation(umkreis: any) {
    if (!umkreis) return null
    const mapped: Record<string, string[]> = {}
    if (umkreis) {
        mapped["Umkreis"] = umkreis
    }

    return mapped
}
function mapAddressInformation(adresseName: any) {
    if (!adresseName) return null
    const mapped: Record<string, string[]> = {}
    if (adresseName) {
        mapped["Adresse"] = adresseName
    }

    return mapped
}

function cleanLocationNameFromPLZ(locationName: string): string {
    return locationName.replace(/\d+/g, "").trim()
}

function isMobileLocationSearch(entry: any): boolean {
    const location = safeParse(entry.location)

    if (!location) return false

    return location?.locationType === "standort"
}
function mapLocationInformation(location: any) {
    if (!location) return null
    const mapped: Record<string, string[]> = {}
    let labelKey = ""

    if (location?.locationType === "ort" || location?.locationType === "region") {
        labelKey = "Ort"
        mapped[labelKey] = location.locationName
    } else if (location?.locationType === "overall") {
        labelKey = "Alle Regionen"
        mapped[labelKey] = []
    } else if (location?.locationType === "plz") {
        labelKey = "PLZ"

        const locationName = cleanLocationNameFromPLZ(location.locationName)

        if (location?.adresseName !== "" && location.plz === "") {
            labelKey = "Ort"
            mapped[labelKey] = [locationName]

            return mapped
        }
        mapped[labelKey] = [locationName]

        mapped[labelKey] = [location.plz + ` (${mapped[labelKey]})`]
    } else if (location?.locationType === "standort") {
        labelKey = "Aktueller Standort"
        mapped[labelKey] = []
    }

    return mapped
}

function mapOpeningInformation(openings: any) {
    if (!openings) return null
    const mapped: Record<string, string[]> = {}

    for (const [_key, value] of Object.entries(openings) as [string, { description: string }][]) {
        if (!value) continue

        if (!mapped["Zeiten"]) {
            mapped["Zeiten"] = []
        }

        mapped["Zeiten"].push(" " + value.description)
    }

    return mapped
}

function mapRubrikInformation(rubrik: any) {
    if (!rubrik) return null
    const mapped: Record<string, string[]> = {}

    for (const [key, value] of Object.entries(rubrik)) {
        if (!value) continue

        if (!mapped["Rubrik"]) {
            mapped["Rubrik"] = []
        }
        mapped["Rubrik"].push(" " + rubrik[key])
    }

    return mapped
}

function mapMerkmalInformation(merkmale: object) {
    if (!merkmale) return null
    const mapped: Record<string, string[]> = {}

    for (const [key, value] of Object.entries(merkmale)) {
        for (const [merkmalKey, merkmalValue] of Object.entries(value.options) as [
            string,
            { merkmalGroup: string; min: number; max: number; description: string },
        ][]) {
            if (!merkmalKey) continue

            if (merkmalValue.merkmalGroup === "Alter") {
                if (!merkmalValue.min) {
                    mapped[merkmalValue.merkmalGroup] = [`bis ${merkmalValue.max} J.`]
                    continue
                }

                if (!merkmalValue.max || merkmalValue.max > 80) {
                    mapped[merkmalValue.merkmalGroup] = [`ab ${merkmalValue.min} J.`]
                    continue
                }

                mapped[key] = [`${merkmalValue.min}-${merkmalValue.max} J.`]
                continue
            }
            if (merkmalValue.merkmalGroup === "Koerpergroesse") {
                if (!merkmalValue.min) {
                    mapped[merkmalValue.merkmalGroup] = [`bis ${merkmalValue.max} cm`]
                    continue
                }

                if (!merkmalValue.max || merkmalValue.max > 220) {
                    mapped[merkmalValue.merkmalGroup] = [`ab ${merkmalValue.min} cm`]
                    continue
                }

                mapped[merkmalValue.merkmalGroup] = [`${merkmalValue.min || 140}-${merkmalValue.max} cm`]
                continue
            }

            if (merkmalValue.merkmalGroup === "Auftrag_TreffenBesuchbar") {
                mapped["Treffpunkte"] ??= []
                mapped["Treffpunkte"].push(" " + merkmalValue.description)
                continue
            }

            if (merkmalValue.merkmalGroup === "Bridge_MitStarsContent") {
                mapped["Exklusive Inhalte"] = ["mit Stars-Content"]
                continue
            }

            if (merkmalValue.merkmalGroup === "Bridge_MitPremiumPosts") {
                mapped["Exklusive Inhalte"].push(" Premium-Beiträge")
                continue
            }

            if (merkmalValue.merkmalGroup === "ClusteredGirls") {
                mapped["Location"] = [merkmalValue.description]
                continue
            }

            if (merkmalValue.merkmalGroup === "Service" && merkmalValue.description === "Mast.") {
                mapped["Service"] ??= []
                mapped["Service"].push(" Masturbation")
                continue
            }

            if (!mapped[merkmalValue.merkmalGroup]) {
                mapped[merkmalValue.merkmalGroup] = []
            }

            mapped[merkmalValue.merkmalGroup].push(" " + merkmalValue.description)
        }
    }

    return mapped
}

function deleteTextBasedOnDeviceType() {
    if (SiteInfo.isMobileVersion) {
        return "Suche entfernen"
    }

    return ""
}

savedCardGeneration().then()
